import type { ComponentType } from "react";
import {
  IconArrowDown,
  IconArrowUp,
  IconAlignCenter,
  IconAlignLeft,
  IconBlockquote,
  IconBold,
  IconChartBar,
  IconCircleCheck,
  IconClearFormatting,
  IconClockHour4,
  IconCopy,
  IconEye,
  IconFileDescription,
  IconItalic,
  IconLink,
  IconList,
  IconListNumbers,
  IconLogin2,
  IconMail,
  IconDots,
  IconPencil,
  IconPhoto,
  IconSearch,
  IconRefresh,
  IconSettings,
  IconFilter,
  IconSquareRoundedMinus,
  IconSquareRoundedPlus,
  IconStar,
  IconTrash,
  IconUnderline,
  IconX,
} from "@tabler/icons-react";

export type NewsletterIconId =
  | "icon-arrow-down"
  | "icon-arrow-up"
  | "icon-align-center"
  | "icon-align-left"
  | "icon-chart"
  | "icon-check-circle"
  | "icon-copy"
  | "icon-draft"
  | "icon-eye"
  | "icon-erase"
  | "icon-format-bold"
  | "icon-format-italic"
  | "icon-format-underline"
  | "icon-image"
  | "icon-link-2"
  | "icon-list"
  | "icon-list-ordered"
  | "icon-login"
  | "icon-mail"
  | "icon-more-horizontal"
  | "icon-minus-box"
  | "icon-pen"
  | "icon-plus-box"
  | "icon-quote"
  | "icon-refresh"
  | "icon-search"
  | "icon-settings"
  | "icon-star"
  | "icon-filter"
  | "icon-time"
  | "icon-trash"
  | "icon-x";

type IconComponentProps = {
  className?: string;
  size?: string | number;
  stroke?: string | number;
  "aria-hidden"?: boolean;
};

const NEWSLETTER_ICONS: Record<NewsletterIconId, ComponentType<IconComponentProps>> = {
  "icon-arrow-down": IconArrowDown,
  "icon-arrow-up": IconArrowUp,
  "icon-align-center": IconAlignCenter,
  "icon-align-left": IconAlignLeft,
  "icon-chart": IconChartBar,
  "icon-check-circle": IconCircleCheck,
  "icon-copy": IconCopy,
  "icon-draft": IconFileDescription,
  "icon-eye": IconEye,
  "icon-erase": IconClearFormatting,
  "icon-format-bold": IconBold,
  "icon-format-italic": IconItalic,
  "icon-format-underline": IconUnderline,
  "icon-image": IconPhoto,
  "icon-link-2": IconLink,
  "icon-list": IconList,
  "icon-list-ordered": IconListNumbers,
  "icon-login": IconLogin2,
  "icon-mail": IconMail,
  "icon-more-horizontal": IconDots,
  "icon-minus-box": IconSquareRoundedMinus,
  "icon-pen": IconPencil,
  "icon-plus-box": IconSquareRoundedPlus,
  "icon-quote": IconBlockquote,
  "icon-refresh": IconRefresh,
  "icon-search": IconSearch,
  "icon-settings": IconSettings,
  "icon-star": IconStar,
  "icon-filter": IconFilter,
  "icon-time": IconClockHour4,
  "icon-trash": IconTrash,
  "icon-x": IconX,
};

export function NewsletterIcon({
  iconId,
  className = "ui-icon",
  size = "1em",
  stroke = 1.9,
}: {
  iconId: NewsletterIconId;
  className?: string;
  size?: string | number;
  stroke?: string | number;
}) {
  const Component = NEWSLETTER_ICONS[iconId];
  return <Component className={className} size={size} stroke={stroke} aria-hidden={true} />;
}

export function NewsletterIconLabel({
  iconId,
  label,
}: {
  iconId: NewsletterIconId;
  label: string;
}) {
  return (
    <span className="icon-label">
      <NewsletterIcon iconId={iconId} />
      <span>{label}</span>
    </span>
  );
}

export function NewsletterIconButton({
  iconId,
  label,
  active = false,
  onClick,
}: {
  iconId: NewsletterIconId;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`icon-button${active ? " is-active" : ""}`}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <NewsletterIcon iconId={iconId} />
    </button>
  );
}
